import React, { useState, useEffect } from 'react';
import {
    Table, Button, Modal, Form, Input,
    Space, message, Spin, Tooltip, Tag
} from 'antd';
import {
    EditOutlined, CopyOutlined, ExpandOutlined,
    ReloadOutlined, InfoCircleOutlined, FileTextOutlined
} from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import adminService from '../../services/adminService';

const { TextArea } = Input;

const PromptsPage = () => {
    const [prompts, setPrompts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [previewModalVisible, setPreviewModalVisible] = useState(false);
    const [previewContent, setPreviewContent] = useState({ key: '', value: '', placeholders: '' });
    const [editingPrompt, setEditingPrompt] = useState(null);
    const [form] = Form.useForm();

    const fetchPrompts = async () => {
        setLoading(true);
        try {
            const data = await adminService.getAllPrompts();
            setPrompts(data);
        } catch (err) {
            message.error('Promptlar yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPrompts();
    }, []);

    const handleEdit = (record) => {
        setEditingPrompt(record);
        form.setFieldsValue({ promptText: record.promptText });
        setModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            await adminService.updatePrompt(editingPrompt.promptKey, values.promptText);
            message.success('Prompt güncellendi');
            setModalVisible(false);
            fetchPrompts();
        } catch (err) {
            if (err.errorFields) return;
            message.error('Güncelleme hatası');
        }
    };

    const handleClearCache = async () => {
        try {
            await adminService.clearPromptCache();
            message.success('Önbellek temizlendi');
        } catch (err) {
            message.error('Önbellek temizleme hatası');
        }
    };

    const handleCopyValue = (value) => {
        navigator.clipboard.writeText(value);
        message.success('Prompt panoya kopyalandı');
    };

    const handlePreview = (record) => {
        setPreviewContent({
            key: record.promptKey,
            value: record.promptText,
            placeholders: record.availablePlaceholders,
            description: record.description
        });
        setPreviewModalVisible(true);
    };

    const columns = [
        {
            title: 'Prompt',
            dataIndex: 'promptKey',
            key: 'promptKey',
            width: 220,
            render: (key, record) => (
                <div>
                    <span className="admin-tag admin-tag-purple">{key}</span>
                    {record.description && (
                        <div style={{
                            color: 'var(--admin-text-tertiary)',
                            fontSize: 12,
                            marginTop: 4
                        }}>
                            {record.description}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'İçerik',
            dataIndex: 'promptText',
            key: 'promptText',
            render: (value, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                        color: 'var(--admin-text-secondary)',
                        maxWidth: 350,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'inline-block',
                        fontFamily: 'monospace',
                        fontSize: 12,
                    }}>
                        {value?.substring(0, 80)}{value?.length > 80 ? '...' : ''}
                    </span>
                    <Tooltip title="Tam içeriği görüntüle">
                        <Button
                            type="text"
                            size="small"
                            icon={<ExpandOutlined />}
                            onClick={() => handlePreview(record)}
                            style={{ color: 'var(--admin-accent-primary)' }}
                        />
                    </Tooltip>
                </div>
            ),
        },
        {
            title: 'Placeholder',
            dataIndex: 'availablePlaceholders',
            key: 'availablePlaceholders',
            width: 180,
            render: (placeholders) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {placeholders ? placeholders.split(',').map(p => (
                        <Tag key={p} color="blue" style={{ fontSize: 11 }}>
                            {`{{${p.trim()}}}`}
                        </Tag>
                    )) : <span style={{ color: 'var(--admin-text-tertiary)' }}>-</span>}
                </div>
            ),
        },
        {
            title: 'İşlemler',
            key: 'actions',
            fixed: 'right',
            width: 120,
            render: (_, record) => (
                <Space size={8}>
                    <Tooltip title="Kopyala">
                        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <Button
                                className="admin-btn-icon"
                                icon={<CopyOutlined />}
                                onClick={() => handleCopyValue(record.promptText)}
                            />
                        </motion.div>
                    </Tooltip>
                    <Tooltip title="Düzenle">
                        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <Button
                                className="admin-btn-icon"
                                icon={<EditOutlined />}
                                onClick={() => handleEdit(record)}
                            />
                        </motion.div>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div>
            {/* Page Header */}
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">LLM Promptları</h1>
                    <p className="admin-page-subtitle">
                        Chatbot'un LLM'e gönderilen prompt şablonlarını yönetin.
                        Placeholder'lar (&#123;&#123;...&#125;&#125;) otomatik doldurulur.
                    </p>
                </div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                        className="admin-btn-secondary"
                        icon={<ReloadOutlined />}
                        onClick={handleClearCache}
                    >
                        Önbelleği Temizle
                    </Button>
                </motion.div>
            </div>

            {/* Info Banner */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                    background: 'rgba(99, 102, 241, 0.1)',
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                    borderRadius: 8,
                    padding: '12px 16px',
                    marginBottom: 20,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                }}
            >
                <InfoCircleOutlined style={{ color: '#6366f1', marginTop: 2 }} />
                <div style={{ fontSize: 13, color: 'var(--admin-text-secondary)', lineHeight: 1.6 }}>
                    <strong style={{ color: 'var(--admin-text-primary)' }}>Placeholder Kullanımı:</strong><br />
                    Prompt içinde <code style={{ background: 'rgba(99,102,241,0.15)', padding: '2px 6px', borderRadius: 4 }}>{'{{INTENT_LIST}}'}</code> gibi
                    ifadeler kullanabilirsiniz. Bu ifadeler çalışma anında gerçek verilerle değiştirilir.
                </div>
            </motion.div>

            {/* Table */}
            <motion.div
                className="admin-table-container"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                {loading ? (
                    <div className="admin-loading">
                        <Spin size="large" />
                    </div>
                ) : (
                    <Table
                        scroll={{ x: 'max-content' }}
                        columns={columns}
                        dataSource={prompts}
                        rowKey="id"
                        pagination={false}
                    />
                )}
            </motion.div>

            {/* Edit Modal */}
            <AnimatePresence>
                {modalVisible && (
                    <Modal
                        title={
                            <div className="admin-modal-heading">
                                <div className="admin-modal-heading-icon">
                                    <EditOutlined />
                                </div>
                                <div>
                                    <div className="admin-modal-heading-title">
                                        Prompt Düzenle
                                        <span className="admin-tag admin-tag-purple" style={{ marginLeft: 10 }}>
                                            {editingPrompt?.promptKey}
                                        </span>
                                    </div>
                                    <div className="admin-modal-heading-subtitle">
                                        LLM'e gönderilen sistem talimatını düzenleyin
                                    </div>
                                </div>
                            </div>
                        }
                        open={modalVisible}
                        onOk={handleSubmit}
                        onCancel={() => setModalVisible(false)}
                        okText="Kaydet"
                        cancelText="İptal"
                        width="92vw"
                        style={{ maxWidth: 900 }}
                        className="admin-modal"
                        rootClassName="admin-modal"
                        okButtonProps={{ className: 'admin-btn-primary' }}
                        cancelButtonProps={{ className: 'admin-btn-secondary' }}
                    >
                        {editingPrompt?.availablePlaceholders && (
                            <div style={{
                                background: 'rgba(99, 102, 241, 0.1)',
                                borderRadius: 8,
                                padding: '10px 14px',
                                marginBottom: 16,
                                fontSize: 13,
                            }}>
                                <strong style={{ color: 'var(--admin-text-primary)' }}>
                                    Kullanılabilir Placeholder'lar:
                                </strong>
                                <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {editingPrompt.availablePlaceholders.split(',').map(p => (
                                        <Tag key={p} color="blue">
                                            {`{{${p.trim()}}}`}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        )}

                        <Form form={form} layout="vertical">
                            <Form.Item
                                name="promptText"
                                label="Prompt İçeriği"
                                rules={[{ required: true, message: 'Prompt içeriği gerekli' }]}
                            >
                                <TextArea
                                    rows={18}
                                    placeholder="Prompt metni..."
                                    style={{
                                        fontFamily: 'monospace',
                                        fontSize: 13,
                                        lineHeight: 1.5,
                                    }}
                                />
                            </Form.Item>
                        </Form>
                    </Modal>
                )}
            </AnimatePresence>

            {/* Preview Modal */}
            <Modal
                title={
                    <div className="admin-modal-heading">
                        <div className="admin-modal-heading-icon">
                            <FileTextOutlined />
                        </div>
                        <div>
                            <div className="admin-modal-heading-title">
                                <span className="admin-tag admin-tag-purple">{previewContent.key}</span>
                            </div>
                            <div className="admin-modal-heading-subtitle">
                                {previewContent.description || 'Prompt içeriğinin tam görünümü'}
                            </div>
                        </div>
                    </div>
                }
                open={previewModalVisible}
                onCancel={() => setPreviewModalVisible(false)}
                footer={[
                    <Button
                        key="copy"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopyValue(previewContent.value)}
                        className="admin-btn-secondary"
                    >
                        Kopyala
                    </Button>,
                    <Button
                        key="edit"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setPreviewModalVisible(false);
                            const record = prompts.find(p => p.promptKey === previewContent.key);
                            if (record) handleEdit(record);
                        }}
                        className="admin-btn-primary"
                    >
                        Düzenle
                    </Button>,
                ]}
                width="92vw"
                style={{ maxWidth: 900 }}
                className="admin-modal"
                rootClassName="admin-modal"
            >
                {previewContent.placeholders && (
                    <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {previewContent.placeholders.split(',').map(p => (
                            <Tag key={p} color="blue" style={{ fontSize: 11 }}>
                                {`{{${p.trim()}}}`}
                            </Tag>
                        ))}
                    </div>
                )}
                <div style={{
                    background: 'var(--admin-bg-tertiary)',
                    borderRadius: 'var(--admin-radius-md)',
                    padding: 20,
                    maxHeight: 500,
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: 13,
                    lineHeight: 1.6,
                    color: 'var(--admin-text-secondary)',
                }}>
                    {previewContent.value}
                </div>
            </Modal>
        </div>
    );
};

export default PromptsPage;
